<template>
  <el-dialog
    append-to-body
    class="daily-task-edit-dialog"
    :title="record ? '编辑 / 改派任务' : '新增日常任务'"
    :visible="visible"
    width="760px"
    :close-on-click-modal="false"
    @close="$emit('update:visible', false)"
  >
    <el-form ref="form" :model="form" :rules="rules" label-width="110px">
      <div class="section-title">基本信息</div>
      <el-form-item label="任务名称" prop="taskName">
        <el-input v-model="form.taskName" maxlength="100" show-word-limit placeholder="请填写任务名称" />
      </el-form-item>
      <el-row :gutter="20">
        <el-col :span="24">
          <el-form-item label="工作类型" prop="workType">
            <el-select v-model="form.workType" placeholder="请选择工作类型">
              <el-option v-for="t in workTypes" :key="t.value" :value="t.value" :label="t.label" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="24">
          <el-form-item label="所属区域" prop="areaId">
            <AreaCascader
              v-model="form.areaId"
              :options="options.zones"
              filterable
              placeholder="请选择所属区域"
              style="width: 100%"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-form-item label="具体位置" prop="workLocation">
        <el-input v-model="form.workLocation" maxlength="500" placeholder="如：B区二层205房" />
      </el-form-item>
      <el-form-item label="工作内容" prop="workContent">
        <el-input
          v-model="form.workContent"
          type="textarea"
          :rows="3"
          maxlength="2000"
          show-word-limit
          placeholder="请填写需要执行的工作内容"
        />
      </el-form-item>
      <el-form-item
        label="截止日期"
        :required="form.workType === 'zyrw'"
        prop="deadlineDate"
      >
        <el-date-picker
          v-model="form.deadlineDate"
          type="date"
          value-format="yyyy-MM-dd"
          placeholder="请选择截止日期"
          style="width: 100%"
        />
      </el-form-item>
      <el-form-item label="备注说明">
        <el-input
          v-model="form.remark"
          type="textarea"
          :rows="3"
          maxlength="2000"
          show-word-limit
          placeholder="对方人员、电话、单位等"
        />
      </el-form-item>
      <el-form-item label="任务图片">
        <ImageUploader
          ref="taskPhotos"
          v-model="form.taskImageFileIds"
          :max="9"
          :show-zoom="true"
          tip="最多上传9张，支持 jpg/png 格式"
        />
      </el-form-item>
      <div class="section-title">分配信息</div>
      <AssignTypeFields :model="form" team-type="task" preset="task" label-width="110px" />
    </el-form>
    <div slot="footer">
      <el-button @click="$emit('update:visible', false)">取消</el-button>
      <el-button type="primary" :loading="saving" @click="save">保存</el-button>
    </div>
  </el-dialog>
</template>
<script>
import ImageUploader from '@/features/components/common-image/ImageUploader.vue';
import { parseImageFileIds } from '@/features/components/common-image/imageFileIdsUtil.js';
import AreaCascader from '@/features/gnmk/components/AreaCascader.vue';
import AssignTypeFields from '@/features/gnmk/components/AssignTypeFields.vue';
import { formatDate } from '@/features/common/utils/dateTimeUtil';
import { dailyTaskService, workTypes } from './daily-task.service';
export default {
  components: { AssignTypeFields, AreaCascader, ImageUploader },
  props: {
    visible: Boolean,
    record: { type: Object, default: null },
    options: { type: Object, required: true }
  },
  data () {
    const record = this.record || {
      workType: 'xc',
      taskName: '',
      areaId: '',
      workLocation: '',
      workContent: '',
      deadlineDate: null,
      remark: '',
      assignType: 'none',
      assignUserIds: '',
      assignTeamId: ''
    };
    return {
      workTypes,
      saving: false,
      form: {
        uuid: record.uuid,
        version: record.version,
        workType: record.workType,
        taskName: record.taskName || '',
        areaId: record.areaId || record.zoneAreaId || '',
        workLocation: record.workLocation,
        workContent: record.workContent,
        deadlineDate: record.deadlineDate ? formatDate(record.deadlineDate) : null,
        taskImageFileIds: parseImageFileIds(record.taskImageFileIds || record.taskImages),
        remark: record.remark || '',
        assignType: record.assignType || 'none',
        assignUserIds: record.assignUserIds ? record.assignUserIds.split(',') : [],
        assignTeamId: record.assignTeamId
      },
      rules: {
        workType: [{ required: true, message: '请选择工作类型' }],
        taskName: [{ required: true, whitespace: true, message: '请填写任务名称' }, { max: 100, message: '任务名称不能超过100字' }],
        areaId: [{ required: true, message: '请选择所属区域' }],
        workLocation: [{ required: true, whitespace: true, message: '请填写具体位置' }],
        workContent: [{ required: true, whitespace: true, message: '请填写工作内容' }],
        deadlineDate: [{
          validator: (rule, value, callback) => {
            if (this.form.workType === 'zyrw' && !value) {
              callback(new Error('重要任务必须填写截止日期'));
            } else {
              callback();
            }
          },
          trigger: 'change'
        }]
      }
    };
  },
  methods: {
    async save () {
      if (this.saving) {
        return;
      }
      try {
        await this.$refs.form.validate();
      } catch (e) {
        return;
      }
      if (
        (this.form.assignType === 'user' && !this.form.assignUserIds.length) ||
        (this.form.assignType === 'team' && !this.form.assignTeamId)
      ) {
        return this.$message.warning('请选择执行人员或小组');
      }
      if (this.$refs.taskPhotos && this.$refs.taskPhotos.isUploading()) {
        return this.$message.warning('请等待任务图片上传完成');
      }
      if (this.form.taskImageFileIds.length > 9) {
        return this.$message.warning('任务图片最多9张');
      }
      this.saving = true;
      try {
        await dailyTaskService.save({
          ...this.form,
          assignUserIds: this.form.assignType === 'user' ? this.form.assignUserIds.join(',') : null,
          assignTeamId: this.form.assignType === 'team' ? this.form.assignTeamId : null
        });
        this.$message.success('任务已保存');
        this.$emit('success');
        this.$emit('update:visible', false);
      } catch (e) {
        this.$message.error('保存失败，请检查提示后重试');
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
<style scoped>
.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 20px;
  padding-left: 10px;
  border-left: 4px solid #409eff;
  line-height: 1.1;
}
.el-select {
  width: 100%;
}
</style>
